import { useState } from 'react'
import { LogOut } from '@digital-twins/design-system/icons'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { populationsApi } from '@/lib/api'

interface LeaveSharedAudienceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  audienceId: string
  audienceName: string
  onLeft: () => void
}

export default function LeaveSharedAudienceDialog({
  open,
  onOpenChange,
  audienceId,
  audienceName,
  onLeft,
}: LeaveSharedAudienceDialogProps) {
  const { t } = useTranslation('app')
  const [leaving, setLeaving] = useState(false)

  const handleLeave = async () => {
    setLeaving(true)
    try {
      await populationsApi.leaveSharedAudience(audienceId)
      toast.success(t('audienceActions.left'))
      onOpenChange(false)
      onLeft()
    } catch {
      toast.error(t('audienceActions.leaveFailed'))
    } finally {
      setLeaving(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-2xl sm:max-w-[440px]">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10">
              <LogOut className="h-4 w-4 text-destructive" />
            </div>
            <AlertDialogTitle>{t('audienceActions.leaveSharedQ')}</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            You'll lose access to{' '}
            <strong className="text-foreground">{audienceName}</strong>{' '}
            immediately. You'll need a new invitation to regain access.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={leaving}>{t('cancel', { ns: 'common' })}</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleLeave}
            disabled={leaving}
            data-testid="leave-audience-confirm"
          >
            {leaving ? t('audienceActions.leaving') : t('audienceActions.leaveConfirm')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
